// old callActivate
import { addDays, endOfDay, format } from 'date-fns'

import { VoucherType } from '@/providers'
import { apiService } from '@/services/apiService'
import { AuthService } from '@/services/authService'
import {
    deviceConfigurationService,
    getBettingEnabled
} from '@/services/deviceConfigurationService'
import { TransactionService } from '@/services/transactionService'
import { Auth } from '@/types/common/httpRequest'
import { VoucherResponse } from '@/types/pos/deposit'
import {
    getPaymentType,
    VOUCHER_TYPE_MAPPING
} from '@/utilities/paymentTypeHelper'
import { PrintSlipRequest } from '@/utilities/posSlipHelper'
import {
    buildLegacyTemplateRenderUrl,
    renderConfiguredTemplateImage
} from '@/utilities/templateRenderer'

export type PaymentActivationError = 'cashAcceptorError' | 'authRequired'

export const activatePaymentSession = async (
    voucherTypeId: string,
    onError: (error: PaymentActivationError) => void,
    sessionId?: string
) => {
    console.log('callActivate')
    try {
        const jwt = await getJwtToken()
        console.log('jwt', jwt)

        if (!jwt) {
            onError('authRequired')
            console.error(
                'No cashier token available for activation, showing error'
            )

            return
        }

        const result = await apiService.activate(jwt, voucherTypeId, sessionId)
        console.log('result', result)
        if (!result.activated) {
            onError('cashAcceptorError')
            console.error('Cash acceptor did not activate, showing error')
        }
    } catch (error) {
        console.error('Activation error:', error)
        onError('cashAcceptorError')
        console.error('Cash acceptor did not activate, showing error')
    }
}

// old callDeactivate
export const deactivatePaymentSession = async (sessionId?: string) => {
    try {
        await apiService.deactivate(sessionId)
    } catch (error) {
        console.error('Deactivation error:', error)
        // Silent failure for deactivate as per requirements
    }
}

export const getJwtToken = async () => {
    const token = AuthService.GetToken(Auth.Cashier)
    if (!token) {
        console.error('No cashier token available for activation')
        return null
    }
    return token.startsWith('Bearer ') ? token.substring(7) : token
}

export const createVoucherPrintObject = (
    voucherResponse: VoucherResponse,
    voucherTypeId: string,
    metadata?: {
        deviceName?: string
        vendorName?: string
        vendorUserId?: string
    }
) => {
    const { moneyTransfer } = voucherResponse

    // Format dates
    const createdAt = format(new Date(moneyTransfer.date), 'dd.MM.yyyy. HH:mm')
    const expiry = format(
        endOfDay(addDays(new Date(moneyTransfer.date), 30)),
        'dd.MM.yyyy. HH:mm'
    )
    const currentTime = format(new Date(), 'dd.MM.yyyy. HH:mm')

    // Format amount
    const formattedAmount = moneyTransfer.amount.toLocaleString('sr-RS', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    })

    // Get voucher type text
    const voucherType =
        VOUCHER_TYPE_MAPPING[
            voucherTypeId as keyof typeof VOUCHER_TYPE_MAPPING
        ] || VOUCHER_TYPE_MAPPING['20'] // Default fallback
    let prefix = ''
    if (voucherType === 'Digital Vaučer') {
        prefix = 'digital/'
    }
    return {
        url: `https://market.vcash.rs/${prefix}?code=${moneyTransfer.voucherCode}`,
        voucherCode: moneyTransfer.voucherCode || '---',
        publicCode: moneyTransfer.moneyTransferCode,
        venueName: moneyTransfer.venue?.name || 'VCash Terminal',
        venueAddress: moneyTransfer.venue?.address || '',
        venueCity: moneyTransfer.venue?.city || '',
        amount: formattedAmount,
        currencyCode: moneyTransfer.currencyCode,
        createdAt,
        expiry,
        currentTime,
        voucherType,
        deviceName: metadata?.deviceName || '',
        device_name: metadata?.deviceName || '',
        vendorName: metadata?.vendorName || '',
        vendorUserId: metadata?.vendorUserId || ''
    }
}

type PrintVoucherOptions = {
    deviceName?: string
    vendorName?: string
    vendorUserId?: string
}

// Retained for callers that explicitly need a legacy URL.
export const buildTemplateRenderUrl = buildLegacyTemplateRenderUrl

// Sends a rendered template URL to the printer via the regular print endpoint,
// applying a 10-second timeout after which the print is assumed failed.
const printTemplateUrl = async (
    printUrl: string,
    sessionId?: string
): Promise<{ success: boolean; message: string }> => {
    let timeoutRef: number | null = null
    const printPromise = apiService.print(printUrl, sessionId)
    const timeoutPromise = new Promise<{ success: boolean; message: string }>(
        (resolve) => {
            timeoutRef = window.setTimeout(() => {
                console.log(
                    '⏰ Print operation timed out after 10 seconds, assuming failed'
                )
                resolve({
                    success: false,
                    message: 'Print operation timed out, assumed failed'
                })
            }, 10000)
        }
    )

    const result: { success: boolean; message: string } = await Promise.race([
        printPromise,
        timeoutPromise
    ])
    if (timeoutRef) {
        clearTimeout(timeoutRef)
    }

    return result
}

export const printVoucherTemplateData = async (
    voucherObject: Record<string, unknown>,
    onError: (error: string) => void,
    sessionId?: string,
    templateName = 'terminal_voucher'
): Promise<{ success: boolean; message: string }> => {
    try {
        const printUrl = await renderConfiguredTemplateImage(
            templateName,
            voucherObject
        )
        if (!printUrl) {
            onError('templateRendererNotConfigured')
            return {
                success: false,
                message: 'Template renderer URL not configured'
            }
        }

        console.log('Printing voucher template:', templateName)
        const result = await printTemplateUrl(printUrl, sessionId)
        console.log('Print result:', result)

        if (!result.success) {
            onError('printFailed')
            return { success: false, message: result.message }
        }

        console.log('Voucher print accepted by device service')
        return { success: true, message: 'Voucher printed successfully' }
    } catch (error) {
        console.error('Error printing voucher:', error)
        onError('printError')
        return { success: false, message: 'Error printing voucher' }
    }
}

// Template name used by the renderer service for the POS payment slip.
export const POS_SLIP_TEMPLATE_NAME = 'pos_slip'

/**
 * Builds a renderer URL for displaying the POS slip on screen (image type,
 * upright orientation). Returns null when the renderer is not configured.
 */
export const buildPosSlipPreviewUrl = async (
    slip: PrintSlipRequest,
    signal?: AbortSignal
): Promise<string | null> =>
    renderConfiguredTemplateImage(
        POS_SLIP_TEMPLATE_NAME,
        slip as unknown as Record<string, unknown>,
        { type: 'png', rotate: 0, signal }
    )

/**
 * Prints the POS payment slip using the `pos_slip` template via the regular
 * print endpoint (not the dedicated print-slip API).
 */
export const printPosSlipTemplate = async (
    slip: PrintSlipRequest,
    onError: (error: string) => void,
    sessionId?: string
): Promise<{ success: boolean; message: string }> => {
    try {
        const printUrl = await renderConfiguredTemplateImage(
            POS_SLIP_TEMPLATE_NAME,
            slip as unknown as Record<string, unknown>
        )
        if (!printUrl) {
            onError('templateRendererNotConfigured')
            return {
                success: false,
                message: 'Template renderer URL not configured'
            }
        }

        const result = await printTemplateUrl(printUrl, sessionId)
        if (!result.success) {
            onError('printFailed')
            return { success: false, message: result.message }
        }

        return { success: true, message: 'POS slip printed successfully' }
    } catch (error) {
        console.error('Error printing POS slip:', error)
        onError('printError')
        return { success: false, message: 'Error printing POS slip' }
    }
}

// Function to print voucher with template renderer
export const printVoucher = async (
    voucherResponse: VoucherResponse,
    voucherTypeId: string,
    onError: (error: string) => void,
    sessionId?: string,
    templateName = 'terminal_voucher',
    options?: PrintVoucherOptions
): Promise<{ success: boolean; message: string }> => {
    try {
        let resolvedDeviceName = options?.deviceName?.trim() || ''
        if (!resolvedDeviceName) {
            const credentials = await apiService.getCredentials(sessionId)
            resolvedDeviceName = credentials?.device_name?.trim() || ''
        }

        const voucherObject = createVoucherPrintObject(
            voucherResponse,
            voucherTypeId,
            {
                deviceName: resolvedDeviceName,
                vendorName: options?.vendorName,
                vendorUserId: options?.vendorUserId
            }
        )

        return printVoucherTemplateData(
            voucherObject,
            onError,
            sessionId,
            templateName
        )
    } catch (error) {
        console.error('Error printing voucher:', error)
        onError('printError')
        return { success: false, message: 'Error printing voucher' }
    }
}

export type BuyVoucherParams = {
    selectedVoucherType: VoucherType | null
    bettingFlow?: string
    sessionId?: string
    productId?: number
    voucherRecreateAttempts: number
    setVoucherRecreateAttempts: (attempts: number) => void
    onError: (error: string) => void
    onSuccess: (data: VoucherResponse) => void
    onPrintVoucher: (voucherData: VoucherResponse) => void
}

export const onBuyVoucher = async ({
    selectedVoucherType,
    bettingFlow,
    sessionId,
    productId,
    setVoucherRecreateAttempts,
    voucherRecreateAttempts,
    onError,
    onSuccess,
    onPrintVoucher
}: BuyVoucherParams) => {
    try {
        // Safety net for the device configuration betting toggle: even if the
        // betting service slipped through the welcome screen, never convert a
        // draft into a betting voucher on a device where betting is disabled.
        // Only digital (gaming/ips) vouchers are allowed in that case.
        if (
            selectedVoucherType === 'betting' &&
            !getBettingEnabled(deviceConfigurationService.getConfiguration())
        ) {
            console.warn(
                '⛔ Betting is disabled by device configuration - refusing to convert draft to a betting voucher'
            )
            onError('voucherCreatedFailed')
            return
        }
        // await new Promise((resolve) => setTimeout(resolve, 1500))
        // if (voucherRecreateAttempts < 5) {
        //     setVoucherRecreateAttempts(voucherRecreateAttempts + 1)
        //     onError('voucherCreatedFailed')
        //     return
        // }
        const createVoucher = await TransactionService.CreateVoucher({
            voucherTypeId: getPaymentType(selectedVoucherType, bettingFlow),
            sessionId,
            productId
        })

        if (!createVoucher || !createVoucher?.moneyTransfer?.voucherCode) {
            console.log('❌ Voucher create failed - no voucher data available')
            setVoucherRecreateAttempts(voucherRecreateAttempts + 1)
            onError('voucherCreatedFailed')

            return
        }

        setVoucherRecreateAttempts(0)
        onSuccess(createVoucher)
        console.log('🔍 DEBUG: voucherData set to:', createVoucher)
        // Print the voucher with the new template renderer
        if (createVoucher && createVoucher?.moneyTransfer?.voucherCode) {
            onPrintVoucher(createVoucher)
        } else {
            console.log('❌ Voucher create failed - no voucher data available')
            onError('voucherCreatedFailed')
        }
    } catch (err) {
        // Check for PROMO_VOUCHER_ONLY_NOT_ALLOWED error
        if (
            typeof err === 'object' &&
            err !== null &&
            'errors' in err &&
            Array.isArray((err as { errors: Array<{ code: string }> }).errors)
        ) {
            const errorResponse = err as { errors: Array<{ code: string }> }
            if (
                errorResponse.errors.find(
                    (e) => e.code === 'PROMO_VOUCHER_ONLY_NOT_ALLOWED'
                ) !== undefined
            ) {
                console.log(
                    '❌ Voucher create failed - promo voucher only not allowed'
                )
                onError('promoVoucherOnlyNotAllowed')
                return
            }
        }

        onError('voucherCreatedFailed')
        console.error('🔍 DEBUG: Error in onBuyVoucher:', err)
        console.log('🔍 DEBUG: voucherData after error:')
    }
}

const RAW_VOUCHER_CODE_PATTERN = /^[A-Z0-9]+(-[A-Z0-9]+){2}$/i

export type VoucherScanErrorType =
    | 'none'
    | 'over-limit'
    | 'invalid'
    | 'used'
    | 'type'
    | 'invalid-type-for-promo'
    | 'promo-already-used'
    | 'promo-requires-additional-amount'
    | 'promo-requires-activation'
    | 'cannot-add-to-promo-session'
    | 'promo-cannot-combine-with-others'
    | 'network'
    | 'other'

const getErrorCode = (error: unknown, code: string): boolean => {
    if (
        typeof error !== 'object' ||
        error === null ||
        !('errors' in error) ||
        !Array.isArray((error as { errors: unknown }).errors)
    ) {
        return false
    }

    return (
        (error as { errors: Array<{ code?: string }> }).errors.find(
            (entry) => entry.code === code
        ) !== undefined
    )
}

export const parseVoucherCodeFromScanResult = (
    scanResult: string
): string | null => {
    const trimmed = scanResult.trim()
    if (!trimmed) {
        return null
    }

    const parseCodeFromUrl = (urlString: string): string | null => {
        try {
            const code = new URL(urlString).searchParams.get('code')?.trim()
            return code || null
        } catch {
            return null
        }
    }

    const directUrlCode = parseCodeFromUrl(trimmed)
    if (directUrlCode) {
        return directUrlCode
    }

    if (trimmed.includes('code=')) {
        const querySource = trimmed.startsWith('?') ? trimmed : `?${trimmed}`
        const queryCode = parseCodeFromUrl(`http://local${querySource}`)
        if (queryCode) {
            return queryCode
        }
    }

    if (!trimmed.includes('://') && !trimmed.includes('/')) {
        if (RAW_VOUCHER_CODE_PATTERN.test(trimmed)) {
            return trimmed
        }
    }

    return null
}

export const getVoucherScanErrorType = (
    error: unknown
): VoucherScanErrorType => {
    if (getErrorCode(error, 'MONEY_TRANSFER_CANNOT_BE_FOUND')) {
        return 'invalid'
    }
    if (getErrorCode(error, 'INVALID_MONEY_TRANSFER')) {
        return 'used'
    }
    if (getErrorCode(error, 'INVALID_VOUCHER_TYPE')) {
        return 'type'
    }
    if (getErrorCode(error, 'PROMO_VOUCHER_REQUIRES_NON_BETTING_TYPE')) {
        return 'invalid-type-for-promo'
    }
    if (getErrorCode(error, 'PROMO_VOUCHER_ALREADY_USED')) {
        return 'promo-already-used'
    }
    if (getErrorCode(error, 'PROMO_VOUCHER_ONLY_NOT_ALLOWED')) {
        return 'promo-requires-additional-amount'
    }
    if (getErrorCode(error, 'DRAFT_AMOUNT_EXCEEDS_THE_LIMIT')) {
        return 'over-limit'
    }
    if (getErrorCode(error, 'VOUCHER_REQUIRES_ACTIVATION')) {
        return 'promo-requires-activation'
    }
    if (getErrorCode(error, 'CANNOT_ADD_VOUCHER_TO_PROMO_SESSION')) {
        return 'cannot-add-to-promo-session'
    }
    if (getErrorCode(error, 'PROMO_VOUCHER_CANNOT_COMBINE_WITH_OTHERS')) {
        return 'promo-cannot-combine-with-others'
    }

    if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        typeof (error as { status: unknown }).status === 'number'
    ) {
        const status = (error as { status: number }).status
        if (status === 0 || status >= 500) {
            return 'network'
        }
        if (status >= 400) {
            return 'invalid'
        }
    }

    if (error instanceof TypeError) {
        return 'invalid'
    }

    return 'other'
}
